import { ArrowRight, Globe, Package, Shield, TrendingUp, Users } from 'lucide-react'
import { STORE_NAME } from '../config'
import { HeroSlider } from '../components/HeroSlider'
import { StatsStrip } from '../components/StatsStrip'
import { CommodityGrid } from '../components/CommodityGrid'
import { FeaturedProducts } from '../components/FeaturedProducts'
import { JourneyTimeline } from '../components/JourneyTimeline'
import { ProcessSteps } from '../components/ProcessSteps'
import { MarketsGrid } from '../components/MarketsGrid'
import { CertificationsStrip } from '../components/CertificationsStrip'
import { Testimonials } from '../components/Testimonials'
import { Faq } from '../components/Faq'

export function HomeView({ segment, onNavigate, onSegmentChange, onSelectProduct, onAdd }) {
  const isRetail = segment === 'retail'

  return (
    <main>
      <HeroSlider onNavigate={onNavigate} onSegmentChange={onSegmentChange} />

      <StatsStrip />

      <CommodityGrid onNavigate={onNavigate} />

      <section className="bg-brand-shell px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <span className="mb-3 inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-navy">
            Why buyers choose us
          </span>
          <h2 className="heading-accent font-display text-2xl font-bold text-brand-ink sm:text-3xl">
            Why trade with {STORE_NAME}?
          </h2>
          <p className="mt-4 max-w-2xl text-brand-slate">
            At {STORE_NAME}, we combine sourcing depth, documentation discipline and logistics control
            so every order arrives on time, in spec, and at the price we quoted.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Globe,
                title: 'Global Sourcing',
                text: `At ${STORE_NAME}, we buy directly from vetted producers and exporters across three continents — no stacked broker margins, no mystery origins. Every supplier in our network is audited before their first shipment and re-scored after every order.`
              },
              {
                icon: Package,
                title: 'Reliable Logistics',
                text: 'Container, IBC, flexitank and palletized deliveries coordinated end-to-end from origin port to your receiving dock. Our logistics desk handles booking, customs documentation and tracking so you get one clear delivery window — not a chain of email threads.'
              },
              {
                icon: Shield,
                title: 'Certified Quality',
                text: 'ISO, Kosher, Halal, Organic and custom compliance documentation available on every shipment. Certificates of analysis, origin and inspection reports are verified by our quality team before any goods leave the producer.'
              },
              {
                icon: TrendingUp,
                title: 'Flexible Volumes',
                text: 'From a single retail case to multi-tonne wholesale contracts, we scale with your demand. Tiered pricing rewards volume, and our consolidation service lets smaller buyers share containers to reach better rates.'
              },
              {
                icon: Users,
                title: 'Trade Experts',
                text: `A multilingual team of sourcing, compliance and freight specialists guides you from first quote to final delivery. At ${STORE_NAME}, you always reach a person who knows your account — not a ticket queue.`
              },
              {
                icon: ArrowRight,
                title: 'Get Started Today',
                text: 'Browse the live catalog, add products to your cart or inquiry list, and receive a landed-cost quote within one business day. Retail orders check out online; wholesale enquiries are answered by our trade desk.',
                cta: true
              }
            ].map((f) => (
              <div key={f.title} className="flex flex-col rounded-2xl border border-brand-slate/10 bg-white p-6 shadow-card transition-all hover:-translate-y-1">
                <f.icon className="h-8 w-8 text-brand-gold" />
                <h3 className="mt-4 font-display text-lg font-semibold text-brand-ink">{f.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-slate">{f.text}</p>
                {f.cta && (
                  <button
                    onClick={() => onNavigate('catalog')}
                    className="mt-5 inline-flex w-fit items-center gap-2 rounded-lg bg-brand-navy px-5 py-2.5 text-sm font-bold text-white transition hover:bg-brand-ocean"
                  >
                    Browse catalog <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeaturedProducts
        segment={segment}
        onSelectProduct={onSelectProduct}
        onAdd={onAdd}
      />

      <JourneyTimeline />

      <ProcessSteps />

      <MarketsGrid />

      <CertificationsStrip />

      <Testimonials />

      <Faq />

      <section className="bg-brand-navy px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl text-center">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Ready to place an order?</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/70">
            {isRetail
              ? 'Browse the retail catalog, build your cart and check out online — or switch to wholesale for volume pricing.'
              : 'Add products to your inquiry list and the Alpha Import Export trade desk will prepare a tailored quote.'}
          </p>
          <button
            onClick={() => onNavigate('catalog')}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brand-gold px-6 py-3 text-sm font-bold text-brand-navy hover:bg-brand-gold-light"
          >
            Start Browsing
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </main>
  )
}
